import { BillingDetailsForm, hasCompleteBillingDetails } from '@/components/billing/BillingDetailsForm'
import { Button } from '@/components/ui/button'
import { CardStep } from '@/features/subscribe/card-step'
import type { BillingProfileData } from '@jobilla/nexus-sdk'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ScreenHeading } from './screen-heading'

type Props = {
	organisationId: string
	profile: BillingProfileData | null
	selectedCardId: string | null
	onSelectCard: (paymentMethodId: string) => void
	onContinue: () => void
}

export function BillingScreen({ organisationId, profile, selectedCardId, onSelectCard, onContinue }: Props) {
	const { t } = useTranslation()
	const hasDetails = profile !== null && hasCompleteBillingDetails(profile)
	const [isEditingDetails, setIsEditingDetails] = useState(!hasDetails)

	return (
		<>
			<ScreenHeading title={t('onboarding.billing.title')} subtitle={t('onboarding.billing.subtitle')} />
			{isEditingDetails || !profile
				? (
					<BillingDetailsForm
						organisationId={organisationId}
						profile={profile}
						submitLabel={hasDetails ? t('common.save') : t('onboarding.continue')}
						onSaved={() => setIsEditingDetails(false)}
						onCancel={hasDetails ? () => setIsEditingDetails(false) : undefined}
					/>
				)
				: (
					<>
						<BillingDetailsSummary profile={profile} onChange={() => setIsEditingDetails(true)} />
						<CardStep
							heading={
								<div className="flex flex-col gap-1">
									<h3 className="text-lg font-semibold text-brand-800">
										{t('onboarding.billing.cardTitle')}
									</h3>
									<p className="text-sm text-muted-foreground">
										{t('onboarding.billing.cardSubtitle')}
									</p>
								</div>
							}
							className="mx-0 max-w-none"
							organisationId={organisationId}
							profile={profile}
							selectedId={selectedCardId}
							onSelect={onSelectCard}
							onContinue={onContinue}
						/>
					</>
				)}
		</>
	)
}

function BillingDetailsSummary({ profile, onChange }: { profile: BillingProfileData; onChange: () => void }) {
	const { t, i18n } = useTranslation()
	const { address } = profile
	const country = address?.country
		? new Intl.DisplayNames([i18n.language], { type: 'region' }).of(address.country.toUpperCase())
		: null
	const addressLine = [address?.line1, [address?.postalCode, address?.city].filter(Boolean).join(' '), country]
		.filter(Boolean)
		.join(', ')

	return (
		<div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4 text-sm">
			<div className="flex min-w-0 flex-col gap-0.5">
				<span className="font-medium">{profile.name}</span>
				<span className="text-muted-foreground">{addressLine}</span>
				<span className="text-muted-foreground">{profile.email}</span>
			</div>
			<Button variant="ghost" size="sm" onClick={onChange}>
				{t('subscribe.confirm.change')}
			</Button>
		</div>
	)
}
